<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h2 class="fw-bold mb-1">Zarządzanie Cennikiem</h2>
        <p class="text-muted m-0">Konfiguracja stawek godzinowych i metod płatności.</p>
      </div>
      <button class="btn btn-warning btn-sm fw-bold" @click="fetchLocationPrices" :disabled="isLoading">
        <font-awesome-icon icon="fa-solid fa-rotate" class="me-1" :spin="isLoading" /> Odśwież
      </button>
    </div>

    <div v-if="isLoading" class="text-center py-5">
      <div class="spinner-border text-warning" role="status">
        <span class="visually-hidden">Ładowanie...</span>
      </div>
      <p class="mt-3 text-muted">Pobieranie cennika...</p>
    </div>

    <div v-else-if="error" class="alert alert-danger d-flex align-items-center" role="alert">
      <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="me-2" />
      <div>{{ error }}</div>
    </div>

    <div v-else class="card border-0 shadow-sm p-4 bg-body-tertiary">
      <div v-if="saveError" class="alert alert-danger alert-dismissible fade show mb-4" role="alert">
        <font-awesome-icon icon="fa-solid fa-triangle-exclamation" class="me-2" />
        {{ saveError }}
        <button type="button" class="btn-close" @click="saveError = null"></button>
      </div>

      <h5 class="fw-bold mb-3">
        <font-awesome-icon icon="fa-solid fa-tags" class="text-warning me-2" />
        Stawki godzinowe
      </h5>

      <div class="row g-3 mb-4 align-items-center">
        <div class="col-auto">
          <label class="form-label fw-bold m-0">Waluta:</label>
        </div>
        <div class="col-auto">
          <input
              type="text"
              class="form-control form-control-sm"
              v-model="currency"
              placeholder="np. PLN"
              style="width: 100px;"
          />
        </div>
      </div>

      <div class="table-responsive mb-3">
        <table class="table table-hover align-middle">
          <thead>
          <tr>
            <th style="width: 150px;">Liczba godzin</th>
            <th>Kwota za h</th>
            <th style="width: 80px;" class="text-end">Akcja</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(item, index) in priceRows" :key="index">
            <td>
              <div class="input-group input-group-sm">
                <input
                    type="number"
                    min="1"
                    class="form-control"
                    v-model.number="item.hour"
                />
                <span class="input-group-text">godz.</span>
              </div>
            </td>
            <td>
              <div class="input-group input-group-sm" style="max-width: 200px;">
                <input
                    type="number"
                    min="0"
                    step="0.01"
                    class="form-control"
                    v-model.number="item.price"
                />
                <span class="input-group-text">{{ currency || 'PLN' }}</span>
              </div>
            </td>
            <td class="text-end">
              <button
                  type="button"
                  class="btn btn-sm btn-outline-danger"
                  @click="removeRow(index)"
                  :disabled="priceRows.length <= 1"
                  title="Usuń wiersz"
              >
                <font-awesome-icon icon="fa-solid fa-trash" />
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <div class="d-flex justify-content-between align-items-center mt-2">
        <button type="button" class="btn btn-sm btn-outline-secondary" @click="addRow">
          <font-awesome-icon icon="fa-solid fa-plus" class="me-1" /> Dodaj godzinę
        </button>

        <button type="button" class="btn btn-primary fw-bold px-4" @click="savePrices" :disabled="isSaving">
          <span v-if="isSaving" class="spinner-border spinner-border-sm me-1"></span>
          <font-awesome-icon v-else icon="fa-solid fa-floppy-disk" class="me-1" /> Zapisz Cennik
        </button>
      </div>
    </div>

    <FullPageAlert
        v-if="successAlert.show"
        :title="successAlert.title"
        :message="successAlert.message"
        @close="successAlert.show = false"
    />
  </div>
</template>

<script>
import { fetchWrapper } from "@/wrappers/api/fetchWrapper";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import FullPageAlert from "@/components/notifications/FullPageAlert.vue";

export default {
  name: 'AdminPrices',
  components: {
    FontAwesomeIcon,
    FullPageAlert
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      error: null,
      saveError: null,
      currency: 'PLN',
      priceRows: [],
      successAlert: {
        show: false,
        title: '',
        message: ''
      }
    }
  },
  mounted() {
    this.fetchLocationPrices();
  },
  methods: {
    async fetchLocationPrices() {
      this.isLoading = true;
      this.error = null;
      this.saveError = null;

      try {
        const data = await fetchWrapper.get('/locations/1');
        const pricesData = data?.prices?.per_hour || {};
        this.currency = pricesData.currency || 'PLN';

        const hoursObj = pricesData.hours || {};
        const rows = Object.keys(hoursObj).map(h => ({
          hour: Number(h),
          price: Number(hoursObj[h])
        }));

        if (rows.length === 0) {
          rows.push({ hour: 1, price: 0 });
        }

        rows.sort((a, b) => a.hour - b.hour);
        this.priceRows = rows;
      } catch (err) {
        this.error = typeof err === 'string' ? err : (err.message || 'Nie udało się pobrać cennika');
      } finally {
        this.isLoading = false;
      }
    },

    addRow() {
      const maxHour = this.priceRows.reduce((max, r) => Math.max(max, r.hour || 0), 0);
      this.priceRows.push({
        hour: maxHour + 1,
        price: 0
      });
    },

    removeRow(index) {
      if (this.priceRows.length > 1) {
        this.priceRows.splice(index, 1);
      }
    },

    async savePrices() {
      this.saveError = null;

      if (this.priceRows.length < 1) {
        this.saveError = 'Wymagane jest ustawienie co najmniej 1 godziny w cenniku.';
        return;
      }

      const hoursMap = {};
      for (const row of this.priceRows) {
        if (!row.hour || row.hour < 1) {
          this.saveError = 'Liczba godzin musi być większa lub równa 1.';
          return;
        }
        if (hoursMap[row.hour]) {
          this.saveError = `Godzina ${row.hour} powtarza się w formularzu.`;
          return;
        }
        hoursMap[String(row.hour)] = Number(row.price) || 0;
      }

      const payload = {
        id: 1,
        prices: {
          per_hour: {
            hours: hoursMap,
            currency: this.currency || 'PLN'
          }
        }
      };

      this.isSaving = true;
      try {
        await fetchWrapper.post('/admin/locations/update', payload);
        this.successAlert = {
          show: true,
          title: 'Sukces',
          message: 'Cennik został pomyślnie zaktualizowany!'
        };
        await this.fetchLocationPrices();
      } catch (err) {
        this.saveError = typeof err === 'string' ? err : (err.message || 'Błąd podczas zapisywania cennika');
      } finally {
        this.isSaving = false;
      }
    }
  }
}
</script>

<style scoped>
.btn-primary {
  background-color: #f0c040 !important;
  border: none !important;
  color: #000 !important;
}

.btn-primary:hover {
  background-color: #ffd966 !important;
}
</style>